import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Mail, Search } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Card, QuietBtn } from "./ui";

const STATUS = {
  needs_regenerate: ["Needs regenerate", "bg-amber-50 text-amber-700 border-amber-200"],
  draft: ["Not approved", "bg-zinc-100 text-zinc-600 border-zinc-200"],
  edited: ["Unapproved changes", "bg-sky-50 text-sky-700 border-sky-200"],
  approved: ["Approved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};
const usd = (v) => `$${(v ?? 0).toFixed(2)}`;

/* Readable preview of a sales page or SEO sections — never raw JSON. */
function DripPreview({ st }) {
  const [mode, setMode] = useState("text");
  return (
    <div className="space-y-3" data-testid="drip-preview">
      <div className="flex gap-2 text-xs">
        {["text", "html"].map((m) => <button key={m} data-testid={`drip-view-${m}`} onClick={() => setMode(m)} className={`px-2 py-0.5 rounded border ${mode === m ? "bg-zinc-800 text-white border-zinc-800" : "border-zinc-200 text-zinc-600"}`}>{m === "text" ? "Plain text" : "HTML"}</button>)}
        <span className="text-zinc-400 ml-auto">Sample merge data · as the recipient sees it</span>
      </div>
      {st.emails.map((e, i) => (
        <div key={i} className="border border-zinc-200 rounded-md p-3" data-testid={`drip-email-${i + 1}`}>
          <p className="text-xs text-zinc-400">Day {e.day}</p>
          <p className="font-semibold text-sm" data-testid={`drip-email-${i + 1}-subject`}>{e.subject}</p>
          {mode === "text" ? <pre className="mt-2 text-xs text-zinc-700 whitespace-pre-wrap font-sans" data-testid={`drip-email-${i + 1}-text`}>{e.text}</pre>
                           : <div className="mt-2 text-sm text-zinc-700 [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline" data-testid={`drip-email-${i + 1}-html`} dangerouslySetInnerHTML={{ __html: e.html }} />}
        </div>
      ))}
      <div className="text-sm"><p className="text-xs uppercase tracking-wide text-zinc-400">Study action per domain</p>
        {Object.entries(st.draft.study_actions).map(([d, t]) => <p key={d} className="mt-1" data-testid={`drip-action-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><span className="font-medium">{d}:</span> <span className="text-zinc-600">{t}</span></p>)}
      </div>
    </div>
  );
}

function Preview({ kind, content, st }) {
  if (!content) return <p className="text-sm text-zinc-400" data-testid={`${kind}-preview-empty`}>No copy yet. Regenerate to write the first draft.</p>;
  if (kind === "drip") return <DripPreview st={st} />;
  if (kind === "seo") return (
    <div className="space-y-3" data-testid="seo-preview">
      {content.sections.map((s, i) => <div key={i}><p className="font-semibold text-sm">{s.title}</p><p className="text-sm text-zinc-600 mt-1 leading-relaxed">{s.body}</p></div>)}
    </div>
  );
  return (
    <div className="space-y-3 text-sm" data-testid="sales-preview">
      <p className="font-semibold">{content.tagline}</p>
      <p className="text-zinc-600">{content.hero}</p>
      <ul className="list-disc pl-5 text-zinc-700 space-y-0.5">{content.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
      <div><p className="text-xs uppercase tracking-wide text-zinc-400">Who it is for</p><p className="text-zinc-700 mt-0.5">{content.who_for || <span className="text-zinc-400">(empty)</span>}</p></div>
      <div className="space-y-1.5">{content.faq.map((f, i) => <div key={i}><p className="font-medium">{f.q}</p><p className="text-zinc-600">{f.a}</p></div>)}</div>
      <p className="text-zinc-500">{content.guarantee_line}</p>
    </div>
  );
}

/* Edit form: raw text with merge fields, one field per input. */
function Editor({ kind, draft, onChange }) {
  const set = (path, v) => onChange(path(structuredClone(draft), v));
  const area = (tid, value, fn, rows = 2) => <textarea data-testid={tid} className="w-full border border-zinc-200 rounded-md p-2 text-sm font-mono" rows={rows} value={value} onChange={(e) => set(fn, e.target.value)} />;
  if (kind === "drip") return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500">Plain text only. HTML is rebuilt on every save. Fixed wording carries merge fields; edit the study actions and the method paragraph.</p>
      {Object.keys(draft.study_actions).map((d) => <label key={d} className="block text-xs text-zinc-500">{d}{area(`drip-edit-action-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`, draft.study_actions[d], (x, v) => { x.study_actions[d] = v; return x; }, 3)}</label>)}
      <label className="block text-xs text-zinc-500">Method paragraph{area("drip-edit-method", draft.method_paragraph, (x, v) => { x.method_paragraph = v; return x; }, 4)}</label>
    </div>
  );
  if (kind === "seo") return (
    <div className="space-y-3">{draft.sections.map((s, i) => (
      <div key={i} className="space-y-1">
        {area(`seo-edit-title-${i + 1}`, s.title, (d, v) => { d.sections[i].title = v; return d; }, 1)}
        {area(`seo-edit-body-${i + 1}`, s.body, (d, v) => { d.sections[i].body = v; return d; }, 6)}
      </div>))}
    </div>
  );
  return (
    <div className="space-y-2">
      {area("sales-edit-tagline", draft.tagline, (d, v) => { d.tagline = v; return d; }, 1)}
      {area("sales-edit-hero", draft.hero, (d, v) => { d.hero = v; return d; })}
      {draft.bullets.map((b, i) => area(`sales-edit-bullet-${i + 1}`, b, (d, v) => { d.bullets[i] = v; return d; }, 1))}
      {area("sales-edit-who-for", draft.who_for, (d, v) => { d.who_for = v; return d; }, 3)}
      {draft.faq.map((f, i) => <div key={i} className="grid grid-cols-2 gap-1">{area(`sales-edit-faq-q-${i + 1}`, f.q, (d, v) => { d.faq[i].q = v; return d; }, 1)}{area(`sales-edit-faq-a-${i + 1}`, f.a, (d, v) => { d.faq[i].a = v; return d; })}</div>)}
      {area("sales-edit-guarantee-line", draft.guarantee_line, (d, v) => { d.guarantee_line = v; return d; }, 1)}
    </div>
  );
}

function Violations({ kind, items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-3 border border-red-200 bg-red-50 rounded-md p-3" data-testid={`${kind}-violations`}>
      <p className="text-xs font-semibold text-red-700">{items.length} rule violation{items.length !== 1 ? "s" : ""}. Copy with violations cannot be approved. There is no override.</p>
      <ul className="mt-1.5 space-y-0.5">{items.map((v, i) => <li key={i} className="text-xs text-red-700" data-testid={`${kind}-violation-${i + 1}`}><span className="font-mono">{v.field}</span>: {v.rule}{v.text ? <> · <span className="italic">"{v.text}"</span></> : null}</li>)}</ul>
    </div>
  );
}

export function CopyCard({ certId, kind, onChanged }) {
  const [st, setSt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/admin/certs/${certId}/marketing/${kind}`;
  const load = useCallback(() => api.get(base).then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e))), [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (st?.job?.status !== "running") return;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [st, load]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setSt(data); if (ok) toast.success(ok); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const regenerate = () => {
    if (!window.confirm(`Regenerate ${st.label}? Estimated cost ${usd(st.estimate_usd)} (${st.mode.mode}). The current draft is replaced; the approved version stays until you approve again.`)) return;
    act(() => api.post(`${base}/regenerate`), st.mode.mode === "mock" ? "Mock copy written ($0.00)" : "Regenerating…");
  };
  const save = () => act(() => api.put(base, { content: draft }), "Draft saved and checked against the rules").then(() => setEditing(false));
  if (!st) return <Card icon={kind === "sales" ? FileText : kind === "drip" ? Mail : Search} title={kind === "sales" ? "Sales page" : kind === "drip" ? "Drip sequence" : "SEO sections"} testid={`${kind}-copy-card`}><p className="text-sm text-zinc-400">Loading…</p></Card>;
  const [label, pill] = STATUS[st.status] || STATUS.draft;
  const running = st.job?.status === "running";
  return (
    <Card icon={kind === "sales" ? FileText : kind === "drip" ? Mail : Search} title={st.label} testid={`${kind}-copy-card`}
          actions={<div className="flex items-center gap-2">
            {st.mock && <span data-testid={`${kind}-mock-badge`} className="px-2 py-0.5 rounded-full border text-xs font-bold bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200">MOCK</span>}
            <span data-testid={`${kind}-status-pill`} className={`px-2.5 py-0.5 rounded-full border text-xs font-medium ${pill}`}>{label}</span>
          </div>}>
      {editing ? <Editor kind={kind} draft={draft} onChange={setDraft} /> : <Preview kind={kind} content={st.preview} st={st} />}
      <Violations kind={kind} items={st.violations} />
      {st.job?.status === "failed" && <p className="mt-2 text-xs text-red-700" data-testid={`${kind}-job-error`}>Regenerate failed: {st.job.error}</p>}
      {st.mode.mode !== "live" && <p className="mt-3 text-xs text-zinc-500" data-testid={`${kind}-mode-note`}>{st.mode.reason}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {editing ? (<>
          <QuietBtn data-testid={`${kind}-save-btn`} disabled={busy} onClick={save}>Save draft</QuietBtn>
          <QuietBtn data-testid={`${kind}-cancel-edit-btn`} disabled={busy} onClick={() => setEditing(false)}>Cancel</QuietBtn>
        </>) : (<>
          <QuietBtn data-testid={`${kind}-regenerate-btn`} disabled={busy || running || st.mode.mode === "disabled"} title={st.mode.reason} onClick={regenerate}>{running ? "Regenerating…" : `Regenerate (est. ${usd(st.estimate_usd)})`}</QuietBtn>
          <QuietBtn data-testid={`${kind}-edit-btn`} disabled={busy || !st.draft} onClick={() => { setDraft(structuredClone(st.draft)); setEditing(true); }}>Edit</QuietBtn>
          <QuietBtn data-testid={`${kind}-approve-btn`} disabled={busy || !st.can_approve} title={st.approve_blockers.join(" ")} onClick={() => act(() => api.post(`${base}/approve`), "Approved")}>Approve</QuietBtn>
          <QuietBtn data-testid={`${kind}-revert-btn`} disabled={busy || !st.can_revert} onClick={() => act(() => api.post(`${base}/revert`), "Reverted to the last approved version")}>Revert to approved</QuietBtn>
          {kind === "drip" && <QuietBtn data-testid="drip-send-test-btn" disabled={busy || !st.draft} onClick={async () => { setBusy(true); try { const { data } = await api.post(`${base}/send-test`); toast.success(`Sent ${data.sent.length} test emails to you (${data.sent[0]?.mode}, stream ${data.sent[0]?.stream})`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } }}>Send test to me</QuietBtn>}
        </>)}
      </div>
      {kind === "drip" && st.counts && <p className="mt-2 text-xs text-zinc-600" data-testid="drip-counts">Started {st.counts.started} · Sent {st.day_offsets.map((d) => `day ${d}: ${st.counts.sent_per_day[String(d)]}`).join(" · ")} · Exited on purchase {st.counts.exited_on_purchase} · Unsubscribed {st.counts.unsubscribed}</p>}
      <p className="mt-2 text-xs text-zinc-400" data-testid={`${kind}-spend-line`}>
        Last regenerate: {usd(st.spend_usd)} actual{st.model ? ` · ${st.model}` : ""}{st.generated_at ? ` · ${st.generated_at.slice(0, 16).replace("T", " ")}` : ""}{st.approved_at ? ` · approved ${st.approved_at.slice(0, 10)}` : ""}
      </p>
      {!st.can_approve && st.approve_blockers.length > 0 && st.status !== "approved" && <p className="mt-1 text-xs text-amber-700" data-testid={`${kind}-approve-blockers`}>{st.approve_blockers.join(" ")}</p>}
    </Card>
  );
}

export function MarketingTab({ certId, onChanged }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="marketing-tab">
      <CopyCard certId={certId} kind="sales" onChanged={onChanged} />
      <CopyCard certId={certId} kind="seo" onChanged={onChanged} />
      <CopyCard certId={certId} kind="drip" onChanged={onChanged} />
    </div>
  );
}
